/**
 * ConnectSheetsModal - Modal for connecting Google Sheets
 *
 * Refactored: useBodyScrollLock, ESC key, touch-friendly targets
 */

import { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  FileSpreadsheet,
  ExternalLink,
  Check,
  Loader2,
  Link2Off,
  RefreshCw,
  ChevronRight,
} from 'lucide-react';
import { useFitnessStore } from '../../stores/fitnessStore';
import { format, parseISO } from 'date-fns';
import { tr } from 'date-fns/locale';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';

const ConnectSheetsModal = ({ isOpen, onClose }) => {
  const {
    isGoogleConnected,
    googleEmail,
    isConnecting,
    initiateGoogleAuth,
    disconnectGoogle,
    availableSpreadsheets,
    connectedSpreadsheets,
    isLoadingSpreadsheets,
    loadAvailableSpreadsheets,
    selectSpreadsheet,
    isAnalyzingSchema,
  } = useFitnessStore();

  // Body scroll lock (iOS-safe)
  useBodyScrollLock(isOpen);

  // Memoized close handler for ESC key
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  // ESC key to close
  useEffect(() => {
    if (!isOpen) return;

    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, handleClose]);

  // Load spreadsheets when connected
  useEffect(() => {
    if (isOpen && isGoogleConnected) {
      loadAvailableSpreadsheets();
    }
  }, [isOpen, isGoogleConnected]);

  if (!isOpen) return null;

  const connectedIds = connectedSpreadsheets.map(s => s.spreadsheet_id);

  return createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/60 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && handleClose()}
    >
      <motion.div
        initial={{ y: '100%', opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: '100%', opacity: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="w-full sm:max-w-lg max-h-[90vh] sm:max-h-[85vh] bg-gray-900 border-t sm:border border-gray-800 rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col"
      >
        {/* Mobile drag indicator */}
        <div className="sm:hidden flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 bg-gray-700 rounded-full" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 sm:p-4 border-b border-gray-800">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="w-9 h-9 sm:w-10 sm:h-10 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <FileSpreadsheet className="w-4 h-4 sm:w-5 sm:h-5 text-green-400" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base sm:text-lg font-semibold text-white truncate">
                Google Sheets Bağlantısı
              </h2>
              <p className="text-xs sm:text-sm text-gray-400 truncate">
                {isGoogleConnected
                  ? `Bağlı: ${googleEmail}`
                  : 'Tablolarınızı senkronize edin'}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2.5 text-gray-400 hover:text-white hover:bg-gray-800 active:bg-gray-700 rounded-lg transition-colors min-h-touch min-w-touch flex items-center justify-center flex-shrink-0"
            aria-label="Kapat"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {!isGoogleConnected ? (
            // Not connected state
            <div className="text-center py-8">
              <div className="w-20 h-20 bg-gray-800 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <img
                  src="https://www.gstatic.com/images/branding/product/2x/sheets_2020q4_48dp.png"
                  alt="Google Sheets"
                  className="w-12 h-12"
                />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">
                Google Sheets'e Bağlanın
              </h3>
              <p className="text-gray-400 mb-6 max-w-sm mx-auto">
                Egzersiz verilerinizi Google Sheets ile senkronize edin. Mevcut
                tablolarınızı okuyun ve yeni antrenmanlarınızı otomatik kaydedin.
              </p>

              <button
                onClick={initiateGoogleAuth}
                disabled={isConnecting}
                className="flex items-center gap-3 mx-auto px-6 py-3 min-h-touch bg-white hover:bg-gray-100 active:bg-gray-200 disabled:bg-gray-300 text-gray-900 font-medium rounded-lg transition-colors"
              >
                {isConnecting ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <img
                    src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                    alt="Google"
                    className="w-5 h-5"
                  />
                )}
                <span>
                  {isConnecting ? 'Bağlanıyor...' : 'Google ile Bağlan'}
                </span>
              </button>

              <p className="text-xs text-gray-500 mt-4 max-w-xs mx-auto">
                Bağlanarak Google Sheets tablolarınıza okuma ve yazma izni
                vermiş olursunuz.
              </p>
            </div>
          ) : (
            // Connected state - show spreadsheets
            <div className="space-y-4">
              {/* Connected spreadsheets */}
              {connectedSpreadsheets.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-400 mb-2 flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-400" />
                    Bağlı Tablolar
                  </h3>
                  <div className="space-y-2">
                    {connectedSpreadsheets.map((sheet) => (
                      <div
                        key={sheet.id}
                        className="flex items-center gap-3 p-3 bg-green-500/10 border border-green-500/30 rounded-lg"
                      >
                        <FileSpreadsheet className="w-5 h-5 text-green-400" />
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium truncate">
                            {sheet.spreadsheet_name}
                          </p>
                          <p className="text-xs text-gray-400">
                            Son senkronizasyon:{' '}
                            {sheet.last_synced_at
                              ? format(parseISO(sheet.last_synced_at), 'd MMM HH:mm', { locale: tr })
                              : 'Henüz yapılmadı'}
                          </p>
                        </div>
                        <span className="px-2 py-1 text-xs bg-green-500/20 text-green-400 rounded-full">
                          Aktif
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Available spreadsheets */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-400">
                    Tablolarınız
                  </h3>
                  <button
                    onClick={loadAvailableSpreadsheets}
                    disabled={isLoadingSpreadsheets}
                    className="p-2 min-h-touch min-w-touch flex items-center justify-center text-gray-400 hover:text-white hover:bg-gray-800 active:bg-gray-700 rounded-lg transition-colors"
                    aria-label="Yenile"
                  >
                    <RefreshCw
                      className={`w-4 h-4 ${isLoadingSpreadsheets ? 'animate-spin' : ''}`}
                    />
                  </button>
                </div>

                {isLoadingSpreadsheets ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 text-orange-400 animate-spin" />
                  </div>
                ) : availableSpreadsheets.length === 0 ? (
                  <div className="text-center py-6 text-gray-500">
                    Henüz Google Sheets tablonuz yok.
                  </div>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {availableSpreadsheets.map((sheet) => {
                      const isConnected = connectedIds.includes(sheet.id);
                      return (
                        <button
                          key={sheet.id}
                          onClick={() => selectSpreadsheet(sheet)}
                          disabled={isAnalyzingSchema}
                          className={`
                            w-full flex items-center gap-3 p-3 min-h-touch rounded-lg transition-colors text-left active:scale-[0.98]
                            ${isConnected
                              ? 'bg-green-900/30 hover:bg-green-900/50 active:bg-green-900/60 border border-green-500/30'
                              : 'bg-gray-800/50 hover:bg-gray-800 active:bg-gray-700'}
                            cursor-pointer
                          `}
                        >
                          <FileSpreadsheet className={`w-5 h-5 flex-shrink-0 ${isConnected ? 'text-green-400' : 'text-gray-400'}`} />
                          <div className="flex-1 min-w-0">
                            <p className="text-white truncate">{sheet.name}</p>
                            <p className="text-xs text-gray-500">
                              {sheet.modifiedTime
                                ? format(parseISO(sheet.modifiedTime), 'd MMM yyyy', { locale: tr })
                                : ''}
                            </p>
                          </div>
                          {isConnected ? (
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span className="text-xs text-green-400 hidden sm:inline">Senkronize Et</span>
                              <Check className="w-4 h-4 text-green-400" />
                            </div>
                          ) : isAnalyzingSchema ? (
                            <Loader2 className="w-5 h-5 text-orange-400 animate-spin flex-shrink-0" />
                          ) : (
                            <ChevronRight className="w-5 h-5 text-gray-500 flex-shrink-0" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Disconnect option */}
              <div className="pt-4 border-t border-gray-800">
                <button
                  onClick={disconnectGoogle}
                  className="flex items-center gap-2 px-3 py-2.5 min-h-touch text-sm text-red-400 hover:text-red-300 hover:bg-red-500/10 active:bg-red-500/20 rounded-lg transition-colors"
                >
                  <Link2Off className="w-4 h-4" />
                  <span>Google Bağlantısını Kaldır</span>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer - with safe area padding */}
        <div className="flex items-center justify-between p-4 border-t border-gray-800 bg-gray-900/50 pb-safe-area">
          <a
            href="https://sheets.google.com"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-3 py-2.5 min-h-touch text-sm text-gray-400 hover:text-white hover:bg-gray-800 active:bg-gray-700 rounded-lg transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
            <span className="hidden sm:inline">Google Sheets'i Aç</span>
            <span className="sm:hidden">Sheets</span>
          </a>
          <button
            onClick={handleClose}
            className="px-4 py-2.5 min-h-touch text-gray-400 hover:text-white hover:bg-gray-800 active:bg-gray-700 rounded-lg transition-colors"
          >
            Kapat
          </button>
        </div>
      </motion.div>
    </motion.div>,
    document.body
  );
};

export default ConnectSheetsModal;
